import React, { memo, useCallback, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Popover,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  ButtonBase,
  Collapse,
  Chip,
} from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  FolderOutlined as GroupIcon,
  LocationOnOutlined as LocationIcon,  Check as CheckIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import type { Company } from '../types/auth';
import { switchCompanyInUrl } from '../utils/navigation';

interface GroupedCompanies {
  groups: {
    id: number;
    name: string;
    locations: {
      id: number;
      name: string;
      companies: Company[];
    }[];
  }[];
  standalone: Company[];
}

const CompanySelector: React.FC = memo(() => {
  const { companies, currentCompany, setCurrentCompany, companiesLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('common', { keyPrefix: 'companySelector' });
  const isRtl = i18n.language === 'ar';

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [expandedLocations, setExpandedLocations] = useState<Set<number>>(new Set());

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Auto-expand the group/location containing the current company
    if (currentCompany?.company_location) {
      const loc = currentCompany.company_location;
      if (loc.id) setExpandedLocations(new Set([loc.id]));
      if (loc.company_group?.id) setExpandedGroups(new Set([loc.company_group.id]));
    }
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchTerm('');
  }, []);

  const toggleGroup = useCallback((groupId: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const toggleLocation = useCallback((locationId: number) => {
    setExpandedLocations(prev => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  }, []);

  const handleCompanySelect = useCallback((selectedCompany: Company) => {
    setCurrentCompany(selectedCompany);
    
    // Navigate to the same page but with the new company ID
    const newUrl = switchCompanyInUrl(location.pathname, selectedCompany.company_id);
    navigate(newUrl, { replace: true });
    handleClose();
  }, [setCurrentCompany, navigate, location.pathname, handleClose]);

  const filteredCompanies = useMemo(() => {
    if (!searchTerm) return companies;
    const term = searchTerm.toLowerCase();
    return companies.filter(c => 
      c.name.toLowerCase().includes(term) ||
      (c.company_location?.name.toLowerCase().includes(term) ?? false) ||
      (c.company_location?.company_group?.name.toLowerCase().includes(term) ?? false)
    );
  }, [companies, searchTerm]);

  const treeData = useMemo(() => {
    const data: GroupedCompanies = {
      groups: [],
      standalone: []
    };

    const groupMap = new Map<number, typeof data.groups[0]>();
    const locationMap = new Map<number, typeof data.groups[0]['locations'][0]>();

    filteredCompanies.forEach(company => {
      const location = company.company_location;
      const group = location?.company_group;

      if (!location || !group) {
        data.standalone.push(company);
        return;
      }

      let groupObj = groupMap.get(group.id);
      if (!groupObj) {
        groupObj = { id: group.id, name: group.name, locations: [] };
        groupMap.set(group.id, groupObj);
        data.groups.push(groupObj);
      }

      let locationObj = locationMap.get(location.id);
      if (!locationObj) {
        locationObj = { id: location.id, name: location.name, companies: [] };
        locationMap.set(location.id, locationObj);
        groupObj.locations.push(locationObj);
      }

      locationObj.companies.push(company);
    });

    data.groups.sort((a, b) => a.name.localeCompare(b.name));
    data.groups.forEach(g => {
      g.locations.sort((a, b) => a.name.localeCompare(b.name));
      g.locations.forEach(l => {
        l.companies.sort((a, b) => a.name.localeCompare(b.name));
      });
    });
    data.standalone.sort((a, b) => a.name.localeCompare(b.name));

    return data;
  }, [filteredCompanies]);

  const isSearching = searchTerm.length > 0;
  const isGroupExpanded = useCallback((groupId: number) => isSearching || expandedGroups.has(groupId), [isSearching, expandedGroups]);
  const isLocationExpanded = useCallback((locationId: number) => isSearching || expandedLocations.has(locationId), [isSearching, expandedLocations]);

  if (companiesLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 2,
        color: 'rgb(var(--an-sidebar-text-rgb) / 0.72)'
      }}>
        <CircularProgress size={16} sx={{ color: 'inherit' }} />
        <Typography variant="caption" sx={{ marginInlineStart: 1 }}>
          {t('loadingCompanies')}
        </Typography>
      </Box>
    );
  }

  if (companies.length === 0) {
    return (
      <Box sx={{ 
        py: 1, 
        px: 2, 
        backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.07)',
        border: '1px solid rgb(var(--an-sidebar-text-rgb) / 0.12)',
        borderRadius: 'var(--an-radius-md)',
        color: 'rgb(var(--an-sidebar-text-rgb) / 0.72)',
        boxShadow: 'inset 0 1px 0 rgb(var(--an-sidebar-text-rgb) / 0.08)'
      }}>
        <Typography variant="caption" textAlign="center">
          {t('noCompaniesAvailable')}
        </Typography>
      </Box>
    );
  }

  if (companies.length === 1) {
    const singleCompany = companies[0];
    return (
      <Box sx={{ 
        py: 1, 
        px: 2, 
        backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.07)',
        border: '1px solid rgb(var(--an-sidebar-text-rgb) / 0.12)',
        borderRadius: 'var(--an-radius-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        boxShadow: 'inset 0 1px 0 rgb(var(--an-sidebar-text-rgb) / 0.08)'
      }}>
        <BusinessIcon sx={{ fontSize: 16, color: 'rgb(var(--an-sidebar-text-rgb) / 0.72)' }} />
        <Box>
          <Typography variant="caption" sx={{ color: 'rgb(var(--an-sidebar-text-rgb) / 0.72)' }}>
            {t('company')}
          </Typography>
          <Typography variant="body2" fontWeight="medium" sx={{ color: 'var(--an-white)' }}>
            {singleCompany.name || singleCompany.company_id}
          </Typography>
        </Box>
      </Box>
    );
  }

  const open = Boolean(anchorEl);
  const id = open ? 'company-selector-popover' : undefined;

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="caption" sx={{ color: 'rgb(var(--an-sidebar-text-rgb) / 0.72)', mb: 0.5, display: 'block' }}>
        {t('selectCompany')}
      </Typography>
      
      <ButtonBase
        aria-describedby={id}
        onClick={handleOpen}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          backgroundColor: open ? 'rgb(var(--an-sidebar-text-rgb) / 0.13)' : 'rgb(var(--an-sidebar-text-rgb) / 0.07)',
          color: 'var(--an-white)',
          border: '1px solid',
          borderColor: open ? 'rgb(var(--an-accent-rgb) / 0.44)' : 'rgb(var(--an-sidebar-text-rgb) / 0.12)',
          borderRadius: 'var(--an-radius-md)',
          p: 1.25,
          minHeight: 62,
          textAlign: isRtl ? 'right' : 'left',
          boxShadow: open ? '0 0 0 3px rgb(var(--an-accent-rgb) / 0.14)' : 'inset 0 1px 0 rgb(var(--an-sidebar-text-rgb) / 0.08)',
          '&:hover': {
            backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.13)',
            borderColor: 'rgb(var(--an-sidebar-text-rgb) / 0.22)',
          },
          '&.Mui-focusVisible': {
            outline: 'none',
            borderColor: 'var(--an-accent)',
            boxShadow: '0 0 0 3px rgb(var(--an-accent-rgb) / 0.18)',
          },
          transition: 'background-color var(--an-transition), border-color var(--an-transition), box-shadow var(--an-transition)',
        }}
      >
        <Box sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {currentCompany?.name || currentCompany?.company_id || ''}
          </Typography>
          {currentCompany?.company_location?.company_group && (
            <Typography variant="caption" sx={{ color: 'rgb(var(--an-sidebar-text-rgb) / 0.56)', display: 'block' }} noWrap>
              {currentCompany.company_location.company_group.name} &gt; {currentCompany.company_location.name}
            </Typography>
          )}
        </Box>
        <ArrowDownIcon sx={{ fontSize: 20, color: open ? 'var(--an-accent)' : 'rgb(var(--an-sidebar-text-rgb) / 0.72)', marginInlineStart: 1, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform var(--an-transition), color var(--an-transition)' }} />
      </ButtonBase>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'right' : 'left',
        }}
        PaperProps={{
          sx: {
            width: anchorEl?.offsetWidth || 280,
            maxWidth: '100vw',
            maxHeight: 450,
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 'var(--an-radius-md)',
            boxShadow: 'var(--an-shadow-lg)',
            bgcolor: 'var(--an-surface)',
          }
        }}
      >
        <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'var(--an-surface)', zIndex: 1, borderBottom: '1px solid', borderColor: 'var(--an-border-light)' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: 'var(--an-surface-muted)', 
            borderRadius: 'var(--an-radius-sm)',
            px: 1.5,
            py: 0.5
          }}>
            <i className="bi bi-search" style={{ color: 'var(--an-text-secondary)', fontSize: 18 }}></i>
            <InputBase
              autoFocus
              fullWidth
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                marginInlineStart: 1, 
                fontSize: '0.875rem',
                '& input': {
                  padding: '8px 0',
                }
              }}
            />
          </Box>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}>
          <List sx={{ p: 0 }}>
            {treeData.groups.map(group => {
              const groupOpen = isGroupExpanded(group.id);
              const groupHasSelected = group.locations.some(l =>
                l.companies.some(c => c.company_id === currentCompany?.company_id)
              );
              return (
                <Box key={group.id} sx={{ mb: 0.5 }}>
                  {/* Group header */}
                  <ListItemButton
                    onClick={() => toggleGroup(group.id)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 'var(--an-radius-sm)',
                      mx: 0.75,
                      gap: 0.5,
                      '&:hover': { bgcolor: 'var(--an-surface-muted)' },
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22,
                      height: 22,
                      borderRadius: 'var(--an-radius-sm)',
                      bgcolor: 'var(--an-primary)',
                      color: 'var(--an-white)',
                      flexShrink: 0,
                    }}>
                      <GroupIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <ListItemText
                      primary={group.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                        color: 'var(--an-text-primary)',
                        noWrap: true,
                      }}
                    />
                    {groupHasSelected && !groupOpen && (
                      <Chip size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} label="â—" />
                    )}
                    {groupOpen
                      ? <ExpandMoreIcon sx={{ fontSize: 18, color: 'var(--an-text-secondary)' }} />
                      : <ChevronRightIcon sx={{ fontSize: 18, color: 'var(--an-text-secondary)', transform: isRtl ? 'rotate(180deg)' : 'none' }} />
                    }
                  </ListItemButton>

                  {/* Locations within group */}
                  <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                    <Box sx={{
                      marginInlineStart: 2.25,
                      borderInlineStart: '2px solid',
                      borderColor: 'var(--an-primary-light)',
                      ml: isRtl ? 0 : 2.25,
                      mr: isRtl ? 2.25 : 0,
                    }}>
                      {group.locations.map(loc => {
                        const locOpen = isLocationExpanded(loc.id);
                        const locHasSelected = loc.companies.some(c => c.company_id === currentCompany?.company_id);
                        return (
                          <Box key={loc.id}>
                            {/* Location header */}
                            <ListItemButton
                              onClick={() => toggleLocation(loc.id)}
                              sx={{
                                py: 0.5,
                                px: 1.5,
                                borderRadius: 'var(--an-radius-sm)',
                                mx: 0.75,
                                gap: 0.5,
                                '&:hover': { bgcolor: 'var(--an-surface-muted)' },
                              }}
                            >
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: 'var(--an-surface-muted)',
                                border: '1.5px solid',
                                borderColor: 'var(--an-border)',
                                flexShrink: 0,
                              }}>
                                <LocationIcon sx={{ fontSize: 12, color: 'var(--an-text-secondary)' }} />
                              </Box>
                              <ListItemText
                                primary={loc.name}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: 500,
                                  color: 'var(--an-text-secondary)',
                                  noWrap: true,
                                  fontSize: '0.8125rem',
                                }}
                              />
                              {locHasSelected && !locOpen && (
                                <Chip size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} label="â—" />
                              )}
                              {locOpen
                                ? <ExpandMoreIcon sx={{ fontSize: 16, color: 'var(--an-text-tertiary)' }} />
                                : <ChevronRightIcon sx={{ fontSize: 16, color: 'var(--an-text-tertiary)', transform: isRtl ? 'rotate(180deg)' : 'none' }} />
                              }
                            </ListItemButton>

                            {/* Companies within location */}
                            <Collapse in={locOpen} timeout="auto" unmountOnExit>
                              <Box sx={{
                                marginInlineStart: 2,
                                borderInlineStart: '1.5px dashed',
                                borderColor: 'var(--an-border)',
                                ml: isRtl ? 0 : 2,
                                mr: isRtl ? 2 : 0,
                                pb: 0.5,
                              }}>
                                {loc.companies.map(company => {
                                  const isSelected = currentCompany?.company_id === company.company_id;
                                  return (
                                    <ListItemButton
                                      key={company.company_id}
                                      onClick={() => handleCompanySelect(company)}
                                      selected={isSelected}
                                      sx={{
                                        py: 0.75,
                                        px: 1.5,
                                        mx: 0.75,
                                        borderRadius: 'var(--an-radius-sm)',
                                        transition: 'background-color var(--an-transition), box-shadow var(--an-transition), color var(--an-transition)',
                                        '&.Mui-selected': {
                                          bgcolor: 'var(--an-primary)',
                                          color: 'var(--an-white)',
                                          '&:hover': { bgcolor: 'var(--an-primary-dark)' },
                                          '& .MuiListItemIcon-root, & .MuiTypography-root': { color: 'inherit' },
                                          boxShadow: 'var(--an-shadow-sm)',
                                        },
                                        '&:hover:not(.Mui-selected)': {
                                          bgcolor: 'var(--an-surface-muted)',
                                        },
                                      }}
                                    >
                                      <ListItemIcon sx={{ minWidth: 28 }}>
                                        <BusinessIcon sx={{ fontSize: 16 }} />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={company.name}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 600 : 400 }}
                                      />
                                      {isSelected && (
                                        <CheckIcon sx={{ fontSize: 16, marginInlineStart: 1 }} />
                                      )}
                                    </ListItemButton>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {treeData.standalone.length > 0 && (
              <>
                {(treeData.groups.length > 0) && <Divider sx={{ my: 1, mx: 1.5 }} />}
                <Box sx={{ px: 1.5, py: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', px: 0.75 }}>
                    {t('standalone')}
                  </Typography>
                </Box>
                {treeData.standalone.map(company => {
                  const isSelected = currentCompany?.company_id === company.company_id;
                  return (
                    <ListItemButton
                      key={company.company_id}
                      onClick={() => handleCompanySelect(company)}
                      selected={isSelected}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        mx: 0.75,
                        borderRadius: 'var(--an-radius-sm)',
                        transition: 'background-color var(--an-transition), box-shadow var(--an-transition), color var(--an-transition)',
                        '&.Mui-selected': {
                          bgcolor: 'var(--an-primary)',
                          color: 'var(--an-white)',
                          '&:hover': { bgcolor: 'var(--an-primary-dark)' },
                          '& .MuiListItemIcon-root, & .MuiTypography-root': { color: 'inherit' },
                          boxShadow: 'var(--an-shadow-sm)',
                        },
                        '&:hover:not(.Mui-selected)': {
                          bgcolor: 'var(--an-surface-muted)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <BusinessIcon sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={company.name}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 600 : 400 }}
                      />
                      {isSelected && (
                        <CheckIcon sx={{ fontSize: 16, marginInlineStart: 1 }} />
                      )}
                    </ListItemButton>
                  );
                })}
              </>
            )}

            {treeData.groups.length === 0 && treeData.standalone.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('noResults')}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Popover>
    </Box>
  );
});

CompanySelector.displayName = 'CompanySelector';

export default CompanySelector;

